import Phaser from 'phaser';
import { BASE_W, COLORS, MAX_LIVES } from '@/config/constants';
import { audioService } from '@/services/audioService';
import type { MathOperation } from '@/types/models';

export class HUD extends Phaser.GameObjects.Container {
  private opText: Phaser.GameObjects.Text;
  private scoreText: Phaser.GameObjects.Text;
  private comboText: Phaser.GameObjects.Text;
  private levelText: Phaser.GameObjects.Text;
  private hearts: Phaser.GameObjects.Text[] = [];
  private timerBar: Phaser.GameObjects.Graphics;
  private timerProgress = 1;
  private speakerIcon!: Phaser.GameObjects.Text;
  private unsubMute: (() => void) | null = null;

  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);

    const safeTop = 90;

    const topPanel = scene.add.graphics();
    topPanel.fillStyle(COLORS.panel, 0.85);
    topPanel.fillRoundedRect(40, safeTop, BASE_W - 80, 220, 32);

    this.opText = scene.add
      .text(BASE_W / 2, safeTop + 110, '?', {
        fontFamily: 'Russo One, system-ui',
        fontSize: '160px',
        color: '#22d3ee',
        align: 'center'
      })
      .setOrigin(0.5);

    this.scoreText = scene.add
      .text(70, safeTop + 30, 'Puntaje 0', {
        fontFamily: 'Russo One, system-ui',
        fontSize: '36px',
        color: '#ffffff'
      })
      .setOrigin(0, 0);

    this.levelText = scene.add
      .text(BASE_W - 70, safeTop + 30, 'Nivel 1', {
        fontFamily: 'Russo One, system-ui',
        fontSize: '36px',
        color: '#ffffff'
      })
      .setOrigin(1, 0);

    this.comboText = scene.add
      .text(BASE_W / 2, safeTop + 240, '', {
        fontFamily: 'Russo One, system-ui',
        fontSize: '32px',
        color: '#fbbf24'
      })
      .setOrigin(0.5);

    for (let i = 0; i < MAX_LIVES; i++) {
      const h = scene.add
        .text(70 + i * 56, safeTop + 188, '❤', {
          fontFamily: 'Inter, system-ui',
          fontSize: '52px',
          color: '#f87171'
        })
        .setOrigin(0, 0.5);
      this.hearts.push(h);
    }

    this.timerBar = scene.add.graphics();
    this.drawTimer();

    // Ícono de bocina (mute toggle) – esquina superior derecha del panel.
    this.speakerIcon = scene.add
      .text(BASE_W - 80, safeTop + 188, audioService.isMuted() ? '🔇' : '🔊', {
        fontFamily: 'Inter, system-ui',
        fontSize: '60px'
      })
      .setOrigin(0.5, 0.5);
    this.speakerIcon.setInteractive({ useHandCursor: true });
    this.speakerIcon.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      audioService.toggleMute();
    });
    this.unsubMute = audioService.onMuteChange((m) => {
      this.speakerIcon.setText(m ? '🔇' : '🔊');
    });

    this.add([topPanel, this.opText, this.scoreText, this.levelText, this.comboText, this.timerBar, this.speakerIcon, ...this.hearts]);
    scene.add.existing(this);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.cleanup());
  }

  private cleanup(): void {
    if (this.unsubMute) {
      this.unsubMute();
      this.unsubMute = null;
    }
  }

  /** ¿El punto (worldX, worldY) cae sobre algún control interactivo del HUD? */
  isPointerOverInteractive(worldX: number, worldY: number): boolean {
    return this.speakerIcon.getBounds().contains(worldX, worldY);
  }

  /** En el tutorial sólo mostramos la operación y la bocina — el resto distrae. */
  setTutorialMode(active: boolean): void {
    this.scoreText.setVisible(!active);
    this.levelText.setVisible(!active);
    this.comboText.setVisible(!active);
    this.timerBar.setVisible(!active);
    this.hearts.forEach((h) => h.setVisible(!active));
  }


  setOperation(op: MathOperation | null): void {
    if (!op) {
      this.opText.setText('?');
      return;
    }
    this.opText.setText(`${op.a} ${op.op} ${op.b}`);
    this.scene.tweens.add({
      targets: this.opText,
      scale: { from: 0.85, to: 1 },
      duration: 200,
      ease: 'Back.out'
    });
  }

  setScore(score: number): void {
    this.scoreText.setText(`Puntaje ${score.toLocaleString('es-MX')}`);
  }

  setLevel(level: number): void {
    this.levelText.setText(`Nivel ${level + 1}`);
  }

  setCombo(combo: number): void {
    if (combo >= 2) {
      this.comboText.setText(`COMBO ×${combo}`);
      this.comboText.setVisible(true);
    } else {
      this.comboText.setVisible(false);
    }
  }

  setLives(lives: number): void {
    this.hearts.forEach((h, i) => {
      h.setAlpha(i < lives ? 1 : 0.15);
    });
  }

  setTimer(progress: number): void {
    this.timerProgress = Phaser.Math.Clamp(progress, 0, 1);
    this.drawTimer();
  }

  private drawTimer(): void {
    this.timerBar.clear();
    const safeTop = 90;
    const w = BASE_W - 160;
    const x = 80;
    const y = safeTop + 270;
    this.timerBar.fillStyle(0xffffff, 0.1);
    this.timerBar.fillRoundedRect(x, y, w, 12, 6);
    const color =
      this.timerProgress > 0.5 ? COLORS.good : this.timerProgress > 0.25 ? COLORS.warn : COLORS.bad;
    this.timerBar.fillStyle(color, 1);
    this.timerBar.fillRoundedRect(x, y, w * this.timerProgress, 12, 6);
  }
}
